// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Project cover photos: validated, centre-cropped and downscaled before they
// ever leave the browser.
//
// The cropping happens at upload rather than in CSS alone, because the home
// screen draws every project at once. A phone photo is twelve megapixels and
// several megabytes; ten of those is a home screen that takes seconds to
// paint and a storage bucket that grows for no reason. What gets stored is
// what gets shown.

/** 16:9 at a size that stays sharp on a retina card and costs ~150KB. */
export const COVER_W = 1200;
export const COVER_H = 675;
const MAX_INPUT_BYTES = 15 * 1024 * 1024;

export function validateCoverFile(file: File): string | null {
  if (!file.type.startsWith("image/")) return "That file isn't an image.";
  if (file.size > MAX_INPUT_BYTES) return "That image is larger than 15MB.";
  return null;
}

/**
 * Decode a file into an image.
 *
 * The object URL is deliberately NOT revoked on success: the caller may put
 * img.src into the DOM, and revoking it first leaves them rendering a URL
 * that no longer resolves — a blank frame with no error anywhere. The caller
 * owns the URL from here and should revoke img.src when it is finished. It
 * IS revoked on failure, where nobody can be holding it.
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that image.")); };
    img.src = url;
  });
}

/**
 * Render a chosen rectangle of the original at the cover size.
 *
 * The editor decides WHICH rectangle -- this only draws it. Cropping before
 * upload rather than in CSS is what keeps the home screen fast: what gets
 * stored is what gets shown, around 150KB rather than a twelve-megapixel
 * phone photo downloaded in full to paint a 90px thumbnail.
 */
export async function exportCrop(
  img: HTMLImageElement,
  src: { x: number; y: number; w: number; h: number },
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = COVER_W;
  canvas.height = COVER_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process that image.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, src.x, src.y, src.w, src.h, 0, 0, COVER_W, COVER_H);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error("Could not process that image."))),
      "image/jpeg", 0.85,
    );
  });
}

// ── Framing maths ────────────────────────────────────────────────────────
// Kept out of the editor component because this is where an off-by-one shows
// up as a sliver of background at the edge of someone's photo.

/** The smallest scale at which the image still covers the frame. */
export function coverScale(imgW: number, imgH: number, frameW: number, frameH: number): number {
  if (!imgW || !imgH) return 1;
  return Math.max(frameW / imgW, frameH / imgH);
}

/**
 * Hold one axis so the frame is never uncovered.
 *
 * Offsets are the image's top-left relative to the frame's, so they run from
 * negative (panned right/down) to zero. When the displayed image is not
 * larger than the frame there is nothing to pan, and 0 is the only answer.
 */
export function clampOffset(offset: number, displayed: number, frame: number): number {
  if (displayed <= frame) return 0;
  return Math.min(0, Math.max(frame - displayed, offset));
}

/** The rectangle of the ORIGINAL image currently inside the frame. */
export function cropRect(
  ox: number, oy: number, scale: number, frameW: number, frameH: number,
): { x: number; y: number; w: number; h: number } {
  // `-ox || 0` keeps a pan of zero from producing -0, which is correct
  // arithmetic and compares unequal to 0 for anyone checking later.
  return { x: (-ox || 0) / scale, y: (-oy || 0) / scale, w: frameW / scale, h: frameH / scale };
}
